import React, { useEffect, useState, useRef } from 'react'
import { string, bool } from 'prop-types'
import { debounce } from 'throttle-debounce'
import { Checkbox } from '@cgtrader/triangle-ui/dist/src/ssr-index'

import MTCaptcha from 'Utils/MTCaptcha'
import getUtmCookies from 'Utils/utmCookies'
import InputField from 'Pods/Shared/NewInputField'
import { run, RuleRunner } from 'Pods/Shared/RuleRunner'
import { required, isEmail } from 'Pods/Shared/Rules/Rules'
import ReactPasswordStrength from 'Pods/Shared/ReactPasswordStrength'

const fieldValidations = [
  RuleRunner('email', 'Email', required, isEmail),
  RuleRunner('password', 'Password', required),
]

function Register({
  customId = '',
  source = null,
  fromCheckout = false,
  captchaId = '',
}) {
  const ReactPasswordStrengthRef = useRef()
  const debouncedAvailabilityCheck = useRef(debounce(500, checkAccountAvailability)).current

  const [fields, setFields] = useState({
    username: '',
    email: '',
    password: '',
    email_confirmation: '',
    gdpr: 'opt_out',
    availabilityNotifications: {
      username: { message: null, type: 'notification' },
      email: { message: null, type: 'notification' },
    },
    validationErrors: {},
    showErrors: false,
  })
  const registrationCaptchaEnabled = window.gon.features && window.gon.features.rcaptcha

  useEffect(() => {
    setFields({ ...fields, validationErrors: run(fields, fieldValidations) })
  }, [])

  function errorFor(field) {
    return fields.validationErrors[field] || ''
  }

  function handleGdprSelect(choice) {
    setFields({ ...fields, gdpr: choice })
  }

  function handleInputChange(e) {
    const newFields = { ...fields, [e.target.name]: e.target.value }

    if (e.target.name === 'username' && !newFields.validationErrors.username) {
      debouncedAvailabilityCheck(newFields.username, 'username', newFields)
    } else if (e.target.name === 'email' && !newFields.validationErrors.email) {
      debouncedAvailabilityCheck(newFields.email, 'email', newFields)
    }

    setFields({ ...newFields, validationErrors: run(newFields, fieldValidations), showErrors: true })
  }

  function handlePasswordChange(state) {
    const newFields = { ...fields, password: state.password }
    const newValidationErrors = run(newFields, fieldValidations)

    if (!state.isValid) {
      newValidationErrors.password = 'Is too weak'
    } else {
      delete newValidationErrors.password
    }

    setFields({ ...newFields, validationErrors: run(newFields, fieldValidations) })
  }

  function checkAccountAvailability(input, type, newFields) {
    axios.get(`/api/internal/users/${type}/availability`, { params: { [type]: input } })
      .then((response) => onCheckAccountAvailabilitySuccess(response, type, newFields))
      .catch((error) => { })
  }

  function onCheckAccountAvailabilitySuccess(response, type, newFields) {
    const capitalizedType = type[0].toUpperCase() + type.substring(1)
    let message
    let messageType

    if (response.data.success) {
      message = `${capitalizedType} is available`
      messageType = 'notification'
    } else {
      message = `${capitalizedType} is taken by another account`
      messageType = 'error'
    }

    setFields({
      ...newFields,
      validationErrors: run(newFields, fieldValidations),
      availabilityNotifications: {
        ...newFields.availabilityNotifications,
        [type]: {
          message, type: messageType,
        },
      },
    })
  }

  function handleSubmit(e) {
    e.preventDefault()

    try {
      setFields({ ...fields, showErrors: true })

      const validationErrorsPresence = Object.getOwnPropertyNames(fields.validationErrors).length !== 0

      const availabilityErrorsPresence = Object.values(
        fields.availabilityNotifications
      ).filter(n => n.type === 'error').length !== 0

      if (validationErrorsPresence || availabilityErrorsPresence) {
        return null
      }

      const {
        referrer,
        raw_utm_string,
      } = getUtmCookies()

      const data = {
        new_user: {
          username: fields.username,
          email: fields.email,
          email_confirmation: fields.email_confirmation, // honeypot field
          password: fields.password,
          gdpr_consent: fields.gdpr,
          source,
        },
        from_checkout: fromCheckout,
        utm_statistics: {
          referrer,
          raw_utm_string,
        },
        ...(registrationCaptchaEnabled && { MTCaptchaToken: window.mtcaptcha.getVerifiedToken(captchaId) }),
      }

      axios.post('/users', data).then(onSubmitSuccess).catch(onSubmitFailure)
    } catch (error) {
      window.sendErrorToSentry(error, {
        tags: { section: 'React register' },
        extra: {
          msg: 'Registration error',
          email:
            fields.gdpr === 'opt_in'
              ? fields.email
              : 'gdpr consent not given',
          gdpr_consent: fields.gdpr,
        },
      })
    }
  }

  function onSubmitSuccess(response) {
    if (typeof response.data === 'string') {
      // Security tool asks for verification.
      document.write(response.data)
    } else if (fromCheckout) {
      window.location = '/cg/checkout/payment'
    } else {
      window.location = response.data.redirectTo
    }
  }

  function onSubmitFailure(xhr) {
    if (registrationCaptchaEnabled) {
      window.mtcaptcha.resetUI(captchaId)
    }
    App.events.publish('app:notification:flash:error', xhr.response.data.errors)
  }

  function renderPasswordError() {
    let errorText

    switch (fields.validationErrors.password) {
      case 'Password is required':
        errorText = 'Password is required'
        break
      case 'Is too weak':
        if (fields.password.length <= 5) {
          errorText = 'Please enter a longer password to continue'
        } else {
          errorText = 'Please enter a stronger password to continue'
        }
        break
      default:
        errorText = ''
        break
    }

    if (errorText.length === 0) { return null }

    return (
      <div className="cgt-input__error-message">
        <span className="text">{errorText}</span>
      </div>
    )
  }

  function getOmniAuthParams() {
    const {
      referrer,
      raw_utm_string,
    } = getUtmCookies()

    return `gdpr_consent=${fields.gdpr}&source=${source}&referrer=${referrer}&raw_utm_string=${encodeURIComponent(raw_utm_string)}`
  }

  const token = document.querySelector('meta[name="csrf-token"]')?.content

  return (
    <div className="user-auth-container">
      <div className="row">
        <div className="col-12">
          <form onSubmit={handleSubmit}>
            <InputField
              label="Email"
              id={`email${customId}`}
              name="email"
              errorText={errorFor('email')}
              availabilityNotification={fields.availabilityNotifications.email}
              showError={fields.showErrors}
              value={fields.email}
              onChange={handleInputChange}
            />
            {/* honeypot field */}
            <InputField
              label="Email Confirmation"
              id={`email_confirmation${customId}`}
              name="email_confirmation"
              errorText={errorFor('email_confirmation')}
              availabilityNotification={fields.availabilityNotifications.email}
              showError={fields.showErrors}
              value={fields.email_confirmation}
              onChange={handleInputChange}
              autoComplete="off"
              customClass="hidden"
            />
            <div className={`cgt-input ${fields.showErrors && fields.validationErrors.password ? 'cgt-input--error' : ''}`}>
              <label htmlFor="password">Password</label>
              <ReactPasswordStrength
                ref={ReactPasswordStrengthRef}
                minLength={6}
                inputProps={{
                  id: `password${customId}`,
                  name: 'password',
                }}
                changeCallback={handlePasswordChange}
              />
              {fields.showErrors && (
                renderPasswordError()
              )}
            </div>
            {registrationCaptchaEnabled && <MTCaptcha captchaId={captchaId} />}
            <div className="row">
              <div className="col-12">
                <Checkbox
                  checkedColor="primary"
                  id="gdpr-checkbox"
                  label="I would like to receive updates on special offers, new features and community news."
                  isChecked={fields.gdpr === 'opt_in'}
                  onChange={(isChecked) => {
                    handleGdprSelect(isChecked ? 'opt_in' : 'opt_out')
                  }}
                />
              </div>
            </div>
            <div className="row">
              <div className="col-12" />
            </div>
            <div className="row">
              <div className="col-12">
                <button
                  className="cgt-button cgt-button--primary cgt-button--width-expand justify-content-center"
                  type="submit"
                >
                  Register
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>

      <div className="row">
        <div className="col-12" />
      </div>
      <div className="row">
        <div className="col-12">
          <form action={encodeURI(`/users/auth/google_oauth2?${getOmniAuthParams()}`)} method="post">
            <button
              type="submit"
              className="cgt-button cgt-button--width-expand cgt-button--social cgt-button--google-plus justify-content-center"
            >
              Continue via Google
            </button>
            {token && <input type="hidden" name="authenticity_token" value={token} />}
          </form>
        </div>
      </div>
      <div className="row">
        <div className="col-12">
          <form action={encodeURI(`/users/auth/facebook?${getOmniAuthParams()}`)} method="post">
            <button
              type="submit"
              className="cgt-button cgt-button--width-expand cgt-button--social cgt-button--facebook justify-content-center"
            >
              Continue via Facebook
            </button>
            {token && <input type="hidden" name="authenticity_token" value={token} />}
          </form>
        </div>
      </div>
      <a href="/users/confirmation/new?reset_confirmation=true" target="_blank" className="cgt-legacy-link">
        Resend confirmation email
      </a>
    </div>
  )
}

Register.propTypes = {
  customId: string,
  source: string,
  fromCheckout: bool,
  captchaId: string,
}

export default Register
